import { useEffect, useMemo, useState } from 'react'
import { useBulkActions } from '@/hooks/useBulkActions'
import { usePeople } from '@/hooks/usePeople'
import { addDays, fmtDate, today } from '@/logic/dates'
import { activeCategories, useCategories } from '@/store/categories'
import { useHousehold } from '@/store/household'
import { useTasks } from '@/store/tasks'
import { stopSelecting, useUi } from '@/store/ui'
import type { Task } from '@/types'
import { ConfirmSheet } from './ConfirmSheet'
import { Sheet } from './Sheet'
import { CalendarIcon, CheckIcon, CloseIcon, LockIcon, TrashIcon } from './icons'
import './SelectionBar.css'

type Picker = 'move' | 'category' | 'assign' | 'scope' | null

/**
 * The Plan tab's Select mode bar, in quick add's place: how many are picked, Cancel, and the actions
 * over all of them: Done, Move, Category, Assign, Private or shared, Delete. Each action ends Select mode.
 */
export function SelectionBar() {
  const selecting = useUi((s) => s.selecting)
  const ids = useUi((s) => s.selected)
  const byId = useTasks((s) => s.byId)
  const tasks = useMemo(() => ids.map((id) => byId[id]).filter((t): t is Task => !!t), [ids, byId])
  const bulk = useBulkActions()
  const [picker, setPicker] = useState<Picker>(null)

  // Escape leaves Select mode, as Cancel does
  useEffect(() => {
    if (!selecting) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !picker) stopSelecting()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [selecting, picker])

  if (!selecting) return null
  const none = tasks.length === 0
  const finish = (run: () => void) => {
    run()
    setPicker(null)
    stopSelecting()
  }

  return (
    <>
      <div className="selbar" role="toolbar" aria-label="Selected tasks">
        <div className="selbar__head">
          <button type="button" className="selbar__cancel" aria-label="Cancel selecting" onClick={stopSelecting}>
            <CloseIcon size={18} weight={2.4} />
          </button>
          <span className="selbar__count" aria-live="polite">
            {none ? 'Tap tasks to select them' : `${tasks.length} selected`}
          </span>
        </div>
        <div className="selbar__actions">
          <button type="button" className="selbar__act" disabled={none} onClick={() => finish(() => bulk.done(tasks))}>
            <CheckIcon size={18} weight={2.6} />
            Done
          </button>
          <button type="button" className="selbar__act" disabled={none} onClick={() => setPicker('move')}>
            <CalendarIcon size={18} />
            Move
          </button>
          <button type="button" className="selbar__act" disabled={none} onClick={() => setPicker('category')}>
            <span className="selbar__dot" aria-hidden="true" />
            Category
          </button>
          <button type="button" className="selbar__act" disabled={none} onClick={() => setPicker('assign')}>
            <span className="selbar__who" aria-hidden="true">
              <span />
              <span />
            </span>
            Assign
          </button>
          <button type="button" className="selbar__act" disabled={none} onClick={() => setPicker('scope')}>
            <LockIcon size={18} />
            Private
          </button>
          <button type="button" className="selbar__act selbar__act--danger" disabled={none} onClick={() => finish(() => bulk.remove(tasks))}>
            <TrashIcon size={18} />
            Delete
          </button>
        </div>
      </div>
      <MoveSheet open={picker === 'move'} onClose={() => setPicker(null)} onPick={(day) => finish(() => bulk.moveTo(tasks, day))} />
      <CategorySheet open={picker === 'category'} onClose={() => setPicker(null)} onPick={(c) => finish(() => bulk.setCategory(tasks, c))} />
      <AssignSheet open={picker === 'assign'} onClose={() => setPicker(null)} onPick={(uid, label) => finish(() => bulk.assign(tasks, uid, label))} />
      <ScopeSheet open={picker === 'scope'} count={tasks.length} onClose={() => setPicker(null)} onPick={(scope) => finish(() => bulk.setScope(tasks, scope))} />
    </>
  )
}

function MoveSheet({ open, onClose, onPick }: { open: boolean; onClose: () => void; onPick: (day: string | null) => void }) {
  const now = today()
  const choices = [
    { label: 'Today', day: now },
    { label: 'Tomorrow', day: addDays(now, 1) },
  ]
  return (
    <Sheet open={open} heading="Move to" variant="dialog" onClose={onClose}>
      <div className="selpick">
        {choices.map((c) => (
          <button key={c.label} type="button" className="selpick__row" onClick={() => onPick(c.day)}>
            <span>{c.label}</span>
            <span className="selpick__hint">{fmtDate(c.day)}</span>
          </button>
        ))}
        <label className="selpick__row selpick__row--date">
          <span>Pick a day</span>
          <input type="date" className="selpick__date" min={now} onChange={(e) => e.target.value && onPick(e.target.value)} />
        </label>
        <button type="button" className="selpick__row" onClick={() => onPick(null)}>
          <span>No day</span>
          <span className="selpick__hint">Off the week, back to No date or Routines</span>
        </button>
      </div>
    </Sheet>
  )
}

function CategorySheet({ open, onClose, onPick }: { open: boolean; onClose: () => void; onPick: (c: ReturnType<typeof activeCategories>[number] | null) => void }) {
  const all = useCategories((s) => s.all)
  const categories = useMemo(() => activeCategories(all), [all])
  return (
    <Sheet open={open} heading="Category" variant="dialog" onClose={onClose}>
      <div className="selpick">
        {categories.map((c) => (
          <button key={c.id} type="button" className="selpick__row" onClick={() => onPick(c)}>
            <span className="selpick__label">
              <span className="chip__dot" style={{ background: `var(--${c.color})` }} />
              {c.name}
              {c.scope === 'private' && <LockIcon size={12} title="Private category" />}
            </span>
          </button>
        ))}
        <button type="button" className="selpick__row" onClick={() => onPick(null)}>
          <span className="selpick__label">
            <span className="chip__dot" style={{ background: 'var(--faint)' }} />
            None
          </span>
        </button>
      </div>
      <p className="selpick__note">A private category goes only on private tasks.</p>
    </Sheet>
  )
}

function AssignSheet({ open, onClose, onPick }: { open: boolean; onClose: () => void; onPick: (uid: string | null, label: string) => void }) {
  const people = usePeople()
  const me = people.names[people.meUid] ?? 'Me'
  return (
    <Sheet open={open} heading="Who's doing it" variant="dialog" onClose={onClose}>
      <div className="selpick">
        <button type="button" className="selpick__row" onClick={() => onPick(people.meUid, me)}>
          {me}
        </button>
        {people.other && (
          <button type="button" className="selpick__row" onClick={() => onPick(people.other!.uid, people.other!.name)}>
            {people.other.name}
          </button>
        )}
        <button type="button" className="selpick__row" onClick={() => onPick(null, 'either of you')}>
          Either of you
        </button>
      </div>
      <p className="selpick__note">Only shared tasks are assigned; private ones stay yours.</p>
    </Sheet>
  )
}

function ScopeSheet({ open, count, onClose, onPick }: { open: boolean; count: number; onClose: () => void; onPick: (scope: 'shared' | 'private') => void }) {
  const askFirst = useHousehold((s) => s.userDoc?.settings.askBeforeSharing !== false)
  const other = usePeople().other?.name ?? 'The other person'
  const [confirming, setConfirming] = useState(false)
  return (
    <>
      <Sheet open={open && !confirming} heading="Private or shared" variant="dialog" onClose={onClose}>
        <div className="selpick">
          <button type="button" className="selpick__row" onClick={() => onPick('private')}>
            <span>Make private</span>
            <span className="selpick__hint">Only you see them</span>
          </button>
          <button type="button" className="selpick__row" onClick={() => (askFirst ? setConfirming(true) : onPick('shared'))}>
            <span>Make shared</span>
            <span className="selpick__hint">You both see them</span>
          </button>
        </div>
      </Sheet>
      <ConfirmSheet
        open={open && confirming}
        heading={`Let ${other} see ${count === 1 ? 'this task' : `${count} tasks`}?`}
        message={`${count === 1 ? 'It stays' : 'They stay'} yours. A private category comes off.`}
        confirmLabel="Make visible"
        onConfirm={() => {
          setConfirming(false)
          onPick('shared')
        }}
        onCancel={() => {
          setConfirming(false)
          onClose()
        }}
      />
    </>
  )
}
